"use client";

import React, { useState } from "react";
import Image from "next/image";
import { urlFor } from "@/sanity/lib/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PortfolioImage } from "@/sanity/lib/getPortfolioSection";
import { ChevronLeft } from "lucide-react";
import { ChevronRight } from "lucide-react";

const IMAGES_PER_LOAD = 8;

interface Props {
  initialImages: PortfolioImage[];
}

export default function PortfolioSection({ initialImages = [] }: Props) {
  const [images, setImages] = useState<PortfolioImage[]>(initialImages);
  const [visibleCount, setVisibleCount] = useState(IMAGES_PER_LOAD);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const handleLoadMore = () => setVisibleCount(prev => prev + IMAGES_PER_LOAD);
  const handleImageClick = (index: number) => setSelectedIndex(index);
  const handleCloseDialog = () => setSelectedIndex(null);
  const handlePrev = () => {
    if (selectedIndex !== null) setSelectedIndex((selectedIndex - 1 + images.length) % images.length);
  };
  const handleNext = () => {
    if (selectedIndex !== null) setSelectedIndex((selectedIndex + 1) % images.length);
  };

  return (
    <section id="moje-prace" className="bg-gray-100 py-16 px-4">
      <h2 className="text-center font-bold text-4xl mb-12">Ukázka mé práce</h2>

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-screen-xl mx-auto">
        {images.slice(0, visibleCount).map((img, index) => (
          <div
            key={img._id}
            className="relative aspect-[4/3] w-full rounded-lg overflow-hidden shadow-sm cursor-pointer transition-transform hover:scale-[1.02]"
            onClick={() => handleImageClick(index)}
          >
            <Image
              src={urlFor(img.image).url()}
              alt={img.title || "Ukázka práce"}
              fill
              style={{ objectFit: "cover" }}
              sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
              priority
            />
          </div>
        ))}
      </div>

      {visibleCount < images.length && (
        <div className="text-center mt-12">
          <Button onClick={handleLoadMore} className="w-48 cursor-pointer">
            Načíst další
          </Button>
        </div>
      )}

      <Dialog open={selectedIndex !== null} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-[1000px] w-full p-0">
          <DialogTitle className="sr-only">Zvětšený náhled obrázku</DialogTitle>
          {selectedIndex !== null && (
            <div className="relative w-full h-[80vh]">
              <Image
                src={urlFor(images[selectedIndex].image).url()}
                alt={images[selectedIndex].title || "Zvětšený obrázek"}
                fill
                style={{ objectFit: "contain" }}
              />
              <button
                onClick={handlePrev}
                className="absolute left-4 top-1/2 -translate-y-1/2 
                  bg-black/50 text-white px-3 py-2 rounded-full 
                  transition-all duration-200 hover:bg-primary hover:scale-110 hover:shadow-lg hover:shadow-black/40 hover:cursor-pointer"
              >
                <ChevronLeft />
              </button>
              <button
                onClick={handleNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 
                  bg-black/50 text-white px-3 py-2 rounded-full 
                  transition-all duration-200 hover:bg-primary hover:scale-110 hover:shadow-lg hover:shadow-black/40 hover:cursor-pointer"
              >
                <ChevronRight />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}